<template>
  <div class="album-list-wrapper">

    <h3 v-if="header">{{ header }}</h3>

    <div v-if="isLoading" class="loading">
      Loading albums...
    </div>

    <template v-else-if="albums.length > 0">
      <!-- Pagination Controls - Top (hidden if hidePagination is true) -->
      <div v-if="!hidePagination" class="pagination-controls">
        <div class="pagination-info">
          <span class="result-count">{{ albums.length }} album{{ albums.length !== 1 ? 's' : '' }}</span>
          <span v-if="totalPages > 1" class="page-indicator">
            Page {{ currentPage }} of {{ totalPages }}
          </span>
        </div>

        <div class="pagination-actions">
          <label class="per-page-label">
            Per page:
            <select v-model.number="itemsPerPage" class="per-page-select">
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
          </label>

          <div v-if="totalPages > 1" class="page-buttons">
            <button
              class="page-btn"
              :disabled="currentPage === 1"
              @click="goToPage(currentPage - 1)"
            >
              ← Prev
            </button>
            <button
              class="page-btn"
              :disabled="currentPage === totalPages"
              @click="goToPage(currentPage + 1)"
            >
              Next →
            </button>
          </div>
        </div>
      </div>

      <!-- Album Cards -->
      <div class="album-cards">
        <AlbumCard
          v-for="album in displayedAlbums"
          :key="album.albumid || album.id"
          :album="album"
          :artistid="artistid"
          :composerid="composerid"
          :titlefilter="titlefilter"
          :organistfilter="organistfilter"
          :composerfilter="composerfilter"
        />
      </div>

      <!-- Pagination Controls - Bottom (if multiple pages and not hidden) -->
      <div v-if="!hidePagination && totalPages > 1" class="pagination-controls pagination-bottom">
        <div class="pagination-info">
          <span class="page-indicator">
            Page {{ currentPage }} of {{ totalPages }}
          </span>
        </div>

        <div class="page-buttons">
          <button
            class="page-btn"
            :disabled="currentPage === 1"
            @click="goToPage(currentPage - 1)"
          >
            ← Prev
          </button>
          <button
            class="page-btn"
            :disabled="currentPage === totalPages"
            @click="goToPage(currentPage + 1)"
          >
            Next →
          </button>
        </div>
      </div>
    </template>

    <div v-else class="no-albums">
      No albums found.
    </div>

  </div>
</template>

<script>
import AlbumCard from './AlbumCard.vue'

export default {
  name: 'AlbumList',
  components: { AlbumCard },
  props: {
    header: String,
    albums: Array,
    artistid: {
      type: String,
      default: ''
    },
    composerid: {
      type: String,
      default: ''
    },
    titlefilter: {
      type: String,
      default: ''
    },
    organistfilter: {
      type: String,
      default: ''
    },
    composerfilter: {
      type: String,
      default: ''
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    hidePagination: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      currentPage: 1,
      itemsPerPage: 25
    }
  },

  computed: {
    totalPages () {
      return Math.ceil(this.albums.length / this.itemsPerPage)
    },

    paginatedAlbums () {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.albums.slice(start, end)
    },

    displayedAlbums () {
      // If pagination is hidden, show all albums. Otherwise use pagination.
      return this.hidePagination ? this.albums : this.paginatedAlbums
    }
  },

  watch: {
    // Reset to page 1 when albums change
    albums () {
      this.currentPage = 1
    },

    // Reset to page 1 when items per page changes
    itemsPerPage () {
      this.currentPage = 1
    }
  },

  methods: {
    goToPage (page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
        // Scroll to top of album list
        this.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }
}
</script>

<style scoped>
.album-list-wrapper {
  margin-top: 1rem;
}

.loading {
  opacity: 0.7;
  font-size: 0.95rem;
  padding: 1rem 0;
}

.no-albums {
  opacity: 0.8;
  font-size: 0.95rem;
  padding: 0.5rem 0;
}

/* Pagination Controls */
.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #2a2a2a;
  border-radius: 8px;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.pagination-bottom {
  margin-top: 1rem;
  margin-bottom: 0;
}

.pagination-info {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.result-count {
  color: #fff;
  font-weight: 500;
  font-size: 0.95rem;
}

.page-indicator {
  color: #aaa;
  font-size: 0.9rem;
}

.pagination-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.per-page-label {
  color: #aaa;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.per-page-select {
  background: #333;
  color: #fff;
  border: 1px solid #555;
  border-radius: 4px;
  padding: 0.4rem 0.6rem;
  font-size: 0.9rem;
  cursor: pointer;
}

.per-page-select:hover {
  border-color: #9cf;
}

.per-page-select:focus {
  outline: none;
  border-color: #9cf;
}

.page-buttons {
  display: flex;
  gap: 0.5rem;
}

.page-btn {
  background: #444;
  color: #fff;
  border: 1px solid #555;
  border-radius: 4px;
  padding: 0.4rem 1rem;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background: #555;
  border-color: #9cf;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.album-cards {
  /* No special styling needed, AlbumCard handles its own styling */
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .pagination-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .pagination-info,
  .pagination-actions {
    justify-content: space-between;
    width: 100%;
  }

  .pagination-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .per-page-label {
    justify-content: space-between;
  }

  .page-buttons {
    justify-content: stretch;
  }

  .page-btn {
    flex: 1;
  }
}
</style>
